import React from 'react'
import {
  IonContent,
  IonPage,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonButton,
  IonIcon
} from '@ionic/react'
import { arrowBackOutline } from 'ionicons/icons'
import { useTranslation } from 'react-i18next'
import PageHeader from '../../components/PageHeader'
import PlatformService from '../../services/platform'

const TermsOfService: React.FC = () => {
  const { t, i18n } = useTranslation()
  const isGerman = i18n.language === 'de'
  const isWeb = PlatformService.isWeb()

  return (
    <IonPage>
      <PageHeader title={t('legal.termsOfService')} />
      <IonContent className="ion-padding">
        <div className="max-w-4xl mx-auto">
          {isWeb && (
            <IonButton
              fill="clear"
              routerLink="/"
              className="mb-4 hidden md:flex"
            >
              <IonIcon icon={arrowBackOutline} slot="start" />
              {t('legal.backToHome')}
            </IonButton>
          )}

          <IonCard>
            <IonCardHeader>
              <IonCardTitle>{t('legal.termsOfService')}</IonCardTitle>
              <p className="text-sm text-gray-600">
                {isGerman ? 'Zuletzt aktualisiert: Juni 2025' : 'Last updated: June 2025'}
              </p>
            </IonCardHeader>
            <IonCardContent>
              <div className="prose prose-sm max-w-none space-y-6">
                
                {isGerman ? (
                  // German Content
                  <>
                    <section>
                      <h2 className="text-lg font-semibold mb-3">1. Annahme der Bedingungen</h2>
                      <p>
                        Durch den Zugriff auf oder die Nutzung von InsightFlow („der Service") erklären Sie sich 
                        damit einverstanden, an diese Nutzungsbedingungen („Bedingungen") gebunden zu sein. Wenn 
                        Sie diesen Bedingungen nicht zustimmen, dürfen Sie den Service nicht nutzen. Diese 
                        Bedingungen gelten für alle Benutzer, einschließlich Coaches und Coachees.
                      </p>
                    </section>

                    <section>
                      <h2 className="text-lg font-semibold mb-3">2. Servicebeschreibung</h2>
                      <p>
                        InsightFlow ist eine Coaching-Plattform, die professionelle Coaches mit ihren Klienten 
                        (Coachees) durch digitale Tools verbindet, einschließlich:
                      </p>
                      <ul className="list-disc pl-6 space-y-1">
                        <li>Sitzungsplanung und -verwaltung</li>
                        <li>Selbstreflexions- und Journaling-Tools</li>
                        <li>Zielsetzung und Fortschrittsverfolgung</li>
                        <li>Fortschrittsvisualisierung und Erkenntnisse</li>
                        <li>Sichere Kommunikationsfunktionen</li>
                      </ul>
                    </section>

                    <section>
                      <h2 className="text-lg font-semibold mb-3">3. Benutzerkonten und Registrierung</h2>
                      
                      <h3 className="text-md font-medium mb-2">3.1 Kontoerstellung</h3>
                      <p>
                        Sie müssen bei der Registrierung genaue, aktuelle und vollständige Informationen angeben. 
                        Sie sind dafür verantwortlich, die Vertraulichkeit Ihrer Kontozugangsdaten zu wahren.
                      </p>

                      <h3 className="text-md font-medium mb-2 mt-4">3.2 Benutzerrollen</h3>
                      <ul className="list-disc pl-6 space-y-1">
                        <li><strong>Coaches:</strong> Professionelle Coaches, die Anleitung und Unterstützung bieten</li>
                        <li><strong>Coachees:</strong> Personen, die Coaching-Services und persönliche Entwicklung suchen</li>
                      </ul>

                      <h3 className="text-md font-medium mb-2 mt-4">3.3 Altersanforderungen</h3>
                      <p>
                        Sie müssen mindestens 16 Jahre alt sein, um diesen Service zu nutzen. Benutzer unter 18 
                        Jahren sollten die Zustimmung der Eltern haben, bevor sie unsere Plattform nutzen.
                      </p>
                    </section>

                    <section>
                      <h2 className="text-lg font-semibold mb-3">4. Benutzerverantwortlichkeiten</h2>
                      
                      <h3 className="text-md font-medium mb-2">4.1 Allgemeines Verhalten</h3>
                      <p>Sie stimmen zu:</p>
                      <ul className="list-disc pl-6 space-y-1">
                        <li>Den Service legal und ethisch zu nutzen</li>
                        <li>Genaue und wahrheitsgemäße Informationen bereitzustellen</li>
                        <li>Die Privatsphäre und Rechte anderer Benutzer zu respektieren</li>
                        <li>Professionelles Verhalten in allen Interaktionen zu wahren</li>
                        <li>Ihr Konto sicher und vertraulich zu halten</li>
                      </ul>

                      <h3 className="text-md font-medium mb-2 mt-4">4.2 Verbotene Aktivitäten</h3>
                      <p>Sie dürfen nicht:</p>
                      <ul className="list-disc pl-6 space-y-1">
                        <li>Unangemessene, beleidigende oder schädliche Inhalte teilen</li>
                        <li>Andere nachahmen oder falsche Informationen bereitstellen</li>
                        <li>Versuchen, unbefugten Zugang zu anderen Konten zu erlangen</li>
                        <li>Die Plattform für illegale Aktivitäten nutzen</li>
                        <li>Die Sicherheit oder Funktionalität des Services beeinträchtigen</li>
                        <li>Geltende Gesetze oder Vorschriften verletzen</li>
                      </ul>
                    </section>

                    <section>
                      <h2 className="text-lg font-semibold mb-3">5. Coach-spezifische Bedingungen</h2>
                      
                      <h3 className="text-md font-medium mb-2">5.1 Berufliche Qualifikationen</h3>
                      <p>
                        Coaches versichern, dass sie über angemessene Qualifikationen, Zertifizierungen oder 
                        Erfahrungen verfügen, um Coaching-Services zu erbringen. InsightFlow überprüft keine 
                        Coach-Referenzen.
                      </p>

                      <h3 className="text-md font-medium mb-2 mt-4">5.2 Coach-Verantwortlichkeiten</h3>
                      <ul className="list-disc pl-6 space-y-1">
                        <li>Professionelle Standards und Ethik einhalten</li>
                        <li>Klientenvertraulichkeit und Privatsphäre respektieren</li>
                        <li>Services in gutem Glauben erbringen</li>
                        <li>Geltende professionelle Standards einhalten</li>
                      </ul>

                      <h3 className="text-md font-medium mb-2 mt-4">5.3 Beziehungsbildung</h3>
                      <p>
                        Coach-Coachee-Beziehungen werden über unser Einladungssystem gebildet. Beide Parteien 
                        müssen der Etablierung einer Coaching-Beziehung auf der Plattform zustimmen.
                      </p>
                    </section>

                    <section>
                      <h2 className="text-lg font-semibold mb-3">6. Haftungsbeschränkung</h2>
                      
                      <h3 className="text-md font-medium mb-2">6.1 Service-Haftungsausschluss</h3>
                      <p>
                        Der Service wird „wie besehen" ohne Gewährleistungen jeglicher Art bereitgestellt. Wir 
                        garantieren nicht die Qualität, Wirksamkeit oder Ergebnisse von Coaching-Beziehungen, 
                        die über unsere Plattform gebildet werden.
                      </p>

                      <h3 className="text-md font-medium mb-2 mt-4">6.2 Haftungsbegrenzungen</h3>
                      <p>
                        Im gesetzlich zulässigen Umfang haftet InsightFlow nicht für indirekte, zufällige, 
                        besondere oder Folgeschäden, die aus Ihrer Nutzung des Services entstehen.
                      </p>

                      <h3 className="text-md font-medium mb-2 mt-4">6.3 Coaching-Beziehungs-Haftungsausschluss</h3>
                      <p>
                        InsightFlow ist eine Technologieplattform, die Coaching-Beziehungen erleichtert. Wir sind 
                        nicht verantwortlich für die Qualität, Sicherheit oder Ergebnisse von Coaching-Services, 
                        die von einzelnen Coaches erbracht werden.
                      </p>
                    </section>

                    <section>
                      <h2 className="text-lg font-semibold mb-3">7. Kündigung</h2>
                      
                      <h3 className="text-md font-medium mb-2">7.1 Freiwillige Kündigung</h3>
                      <p>
                        Sie können Ihr Konto jederzeit über die Kontoeinstellungen kündigen. Nach der Kündigung 
                        werden Ihre Daten gemäß unserer Datenschutzrichtlinie gelöscht.
                      </p>

                      <h3 className="text-md font-medium mb-2 mt-4">7.2 Unfreiwillige Kündigung</h3>
                      <p>
                        Wir können Konten wegen Verstößen gegen diese Bedingungen, illegaler Aktivitäten oder 
                        anderen Gründen nach unserem Ermessen sperren oder kündigen.
                      </p>
                    </section>

                    <section>
                      <h2 className="text-lg font-semibold mb-3">8. Änderungen der Bedingungen</h2>
                      <p>
                        Wir können diese Bedingungen jederzeit ändern. Wesentliche Änderungen werden über den 
                        Service oder per E-Mail kommuniziert. Die fortgesetzte Nutzung nach Änderungen stellt 
                        die Annahme neuer Bedingungen dar.
                      </p>
                    </section>

                    <section>
                      <h2 className="text-lg font-semibold mb-3">9. Kontaktinformationen</h2>
                      <p>
                        Für Fragen zu diesen Bedingungen kontaktieren Sie uns:
                      </p>
                      <ul className="list-none space-y-1 mt-2">
                        <li><strong>E-Mail:</strong> legal@insightflow.app</li>
                        <li><strong>Adresse:</strong> InsightFlow Team, [Ihre Adresse]</li>
                        <li><strong>Support:</strong> support@insightflow.app</li>
                      </ul>
                    </section>

                    <section className="mt-8 p-4 bg-blue-50 rounded-lg">
                      <h2 className="text-lg font-semibold mb-3">Wichtiger Hinweis</h2>
                      <p className="text-sm">
                        <strong>Coaching-Haftungsausschluss:</strong> InsightFlow bietet eine Plattform für 
                        Coaching-Beziehungen, erbringt aber nicht direkt Coaching-Services. Einzelne Coaches 
                        sind für ihr professionelles Verhalten und ihre Servicequalität verantwortlich. Wenn 
                        Sie therapeutische oder medizinische Unterstützung benötigen, wenden Sie sich bitte 
                        an qualifizierte Gesundheitsfachkräfte.
                      </p>
                    </section>
                  </>
                ) : (
                  // English Content
                  <>
                    <section>
                      <h2 className="text-lg font-semibold mb-3">1. Acceptance of Terms</h2>
                      <p>
                        By accessing or using InsightFlow ("the Service"), you agree to be bound by these 
                        Terms of Service ("Terms"). If you do not agree to these Terms, you may not use 
                        the Service. These Terms apply to all users, including coaches and coachees.
                      </p>
                    </section>

                    <section>
                      <h2 className="text-lg font-semibold mb-3">2. Description of Service</h2>
                      <p>
                        InsightFlow is a coaching platform that connects professional coaches with their 
                        clients (coachees) through digital tools including:
                      </p>
                      <ul className="list-disc pl-6 space-y-1">
                        <li>Session scheduling and management</li>
                        <li>Self-reflection and journaling tools</li>
                        <li>Goal setting and progress tracking</li>
                        <li>Progress visualization and insights</li>
                        <li>Secure communication features</li>
                      </ul>
                    </section>

                    <section>
                      <h2 className="text-lg font-semibold mb-3">3. User Accounts and Registration</h2>
                      
                      <h3 className="text-md font-medium mb-2">3.1 Account Creation</h3>
                      <p>
                        You must provide accurate, current, and complete information during registration. 
                        You are responsible for maintaining the confidentiality of your account credentials.
                      </p>

                      <h3 className="text-md font-medium mb-2 mt-4">3.2 User Roles</h3>
                      <ul className="list-disc pl-6 space-y-1">
                        <li><strong>Coaches:</strong> Professional coaches who provide guidance and support</li>
                        <li><strong>Coachees:</strong> Individuals seeking coaching services and personal development</li>
                      </ul>

                      <h3 className="text-md font-medium mb-2 mt-4">3.3 Age Requirements</h3>
                      <p>
                        You must be at least 16 years old to use this Service. Users under 18 should have 
                        parental consent before using our platform.
                      </p>
                    </section>

                    <section>
                      <h2 className="text-lg font-semibold mb-3">4. User Responsibilities</h2>
                      
                      <h3 className="text-md font-medium mb-2">4.1 General Conduct</h3>
                      <p>You agree to:</p>
                      <ul className="list-disc pl-6 space-y-1">
                        <li>Use the Service legally and ethically</li>
                        <li>Provide accurate and truthful information</li>
                        <li>Respect other users' privacy and rights</li>
                        <li>Maintain professional conduct in all interactions</li>
                        <li>Keep your account secure and confidential</li>
                      </ul>

                      <h3 className="text-md font-medium mb-2 mt-4">4.2 Prohibited Activities</h3>
                      <p>You may not:</p>
                      <ul className="list-disc pl-6 space-y-1">
                        <li>Share inappropriate, offensive, or harmful content</li>
                        <li>Impersonate others or provide false information</li>
                        <li>Attempt to gain unauthorized access to other accounts</li>
                        <li>Use the platform for illegal activities</li>
                        <li>Interfere with the Service's security or functionality</li>
                        <li>Violate any applicable laws or regulations</li>
                      </ul>
                    </section>

                    <section>
                      <h2 className="text-lg font-semibold mb-3">5. Coach-Specific Terms</h2>
                      
                      <h3 className="text-md font-medium mb-2">5.1 Professional Qualifications</h3>
                      <p>
                        Coaches represent that they have appropriate qualifications, certifications, or 
                        experience to provide coaching services. InsightFlow does not verify coach credentials.
                      </p>

                      <h3 className="text-md font-medium mb-2 mt-4">5.2 Coach Responsibilities</h3>
                      <ul className="list-disc pl-6 space-y-1">
                        <li>Maintain professional standards and ethics</li>
                        <li>Respect client confidentiality and privacy</li>
                        <li>Provide services in good faith</li>
                        <li>Comply with applicable professional standards</li>
                      </ul>

                      <h3 className="text-md font-medium mb-2 mt-4">5.3 Relationship Formation</h3>
                      <p>
                        Coach-coachee relationships are formed through our invitation system. Both parties 
                        must consent to establish a coaching relationship on the platform.
                      </p>
                    </section>

                    <section>
                      <h2 className="text-lg font-semibold mb-3">6. Limitation of Liability</h2>
                      
                      <h3 className="text-md font-medium mb-2">6.1 Service Disclaimer</h3>
                      <p>
                        The Service is provided "as is" without warranties of any kind. We do not guarantee 
                        the quality, effectiveness, or outcomes of coaching relationships formed through our platform.
                      </p>

                      <h3 className="text-md font-medium mb-2 mt-4">6.2 Liability Limits</h3>
                      <p>
                        To the maximum extent permitted by law, InsightFlow shall not be liable for any 
                        indirect, incidental, special, or consequential damages arising from your use of the Service.
                      </p>

                      <h3 className="text-md font-medium mb-2 mt-4">6.3 Coaching Relationship Disclaimer</h3>
                      <p>
                        InsightFlow is a technology platform that facilitates coaching relationships. We are 
                        not responsible for the quality, safety, or outcomes of coaching services provided by 
                        individual coaches.
                      </p>
                    </section>

                    <section>
                      <h2 className="text-lg font-semibold mb-3">7. Termination</h2>
                      
                      <h3 className="text-md font-medium mb-2">7.1 Voluntary Termination</h3>
                      <p>
                        You may terminate your account at any time through account settings. Upon termination, 
                        your data will be deleted according to our Privacy Policy.
                      </p>

                      <h3 className="text-md font-medium mb-2 mt-4">7.2 Involuntary Termination</h3>
                      <p>
                        We may suspend or terminate accounts for violations of these Terms, illegal activity, 
                        or other reasons at our discretion.
                      </p>
                    </section>

                    <section>
                      <h2 className="text-lg font-semibold mb-3">8. Changes to Terms</h2>
                      <p>
                        We may modify these Terms at any time. Material changes will be communicated through 
                        the Service or via email. Continued use after changes constitutes acceptance of new Terms.
                      </p>
                    </section>

                    <section>
                      <h2 className="text-lg font-semibold mb-3">9. Contact Information</h2>
                      <p>
                        For questions about these Terms, contact us:
                      </p>
                      <ul className="list-none space-y-1 mt-2">
                        <li><strong>Email:</strong> legal@insightflow.app</li>
                        <li><strong>Address:</strong> InsightFlow Team, [Your Address]</li>
                        <li><strong>Support:</strong> support@insightflow.app</li>
                      </ul>
                    </section>

                    <section className="mt-8 p-4 bg-blue-50 rounded-lg">
                      <h2 className="text-lg font-semibold mb-3">Important Notice</h2>
                      <p className="text-sm">
                        <strong>Coaching Disclaimer:</strong> InsightFlow provides a platform for coaching 
                        relationships but does not provide coaching services directly. Individual coaches are 
                        responsible for their professional conduct and service quality. If you need therapeutic 
                        or medical support, please consult qualified healthcare professionals.
                      </p>
                    </section>
                  </>
                )}

              </div>
            </IonCardContent>
          </IonCard>
        </div>
      </IonContent>
    </IonPage>
  )
}

export default TermsOfService 